/**
 * A colour made deeper the way the eye reads it (6.7): in OKLCH, only the
 * lightness moves — the hue and the chroma stay, so Ei!'s gold deepens into a
 * darker gold, never the olive-brown that mixing its red, green and blue toward
 * black gives. Where a deeper shade of that chroma does not exist in sRGB, the
 * chroma is the most the screen can show at that lightness (CSS Color 4's
 * gamut mapping, by chroma reduction), the hue kept.
 *
 * OKLab: Björn Ottosson, 2020 — the matrices of CSS Color 4.
 */

export interface Oklch {
  l: number;
  c: number;
  h: number;
}

const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const toGamma = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);

function rgbOf(hex: string): [number, number, number] {
  const h = hex.slice(1);
  const full = h.length === 3 ? [...h].map((c) => c + c).join('') : h;
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) / 255) as [number, number, number];
}

/** A `#rrggbb` colour in OKLCH: lightness 0–1, chroma, hue in radians. */
export function oklchOf(hex: string): Oklch {
  const [r, g, b] = rgbOf(hex).map(toLinear) as [number, number, number];
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return { l: L, c: Math.hypot(A, B), h: Math.atan2(B, A) };
}

/** Linear sRGB of an OKLCH colour — channels may fall outside 0–1 (out of gamut). */
function linearRgbOf({ l: L, c, h }: Oklch): [number, number, number] {
  const A = c * Math.cos(h);
  const B = c * Math.sin(h);
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s = (L - 0.0894841775 * A - 1.291485548 * B) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

const EPSILON = 1e-6;
const inGamut = (rgb: number[]) => rgb.every((v) => v >= -EPSILON && v <= 1 + EPSILON);

/** The OKLCH colour as `#rrggbb`, its chroma reduced (hue and lightness kept) until sRGB holds it. */
function hexOf(colour: Oklch): string {
  let rgb = linearRgbOf(colour);
  if (!inGamut(rgb)) {
    let [low, high] = [0, colour.c];
    for (let i = 0; i < 24; i++) {
      const mid = (low + high) / 2;
      if (inGamut(linearRgbOf({ ...colour, c: mid }))) low = mid;
      else high = mid;
    }
    rgb = linearRgbOf({ ...colour, c: low });
  }
  return `#${rgb
    .map((v) => Math.round(toGamma(Math.min(1, Math.max(0, v))) * 255))
    .map((v) => v.toString(16).padStart(2, '0'))
    .join('')}`;
}

/**
 * The colour with its OKLCH lightness moved by `by` — deeper below zero,
 * lighter above — kept within 0–1, the hue and the chroma kept.
 */
export function shiftLightness(hex: string, by: number): string {
  const colour = oklchOf(hex);
  return hexOf({ ...colour, l: Math.min(1, Math.max(0, colour.l + by)) });
}
